import { useState, useCallback } from 'preact/hooks'
import { LogOut, Loader2 } from 'lucide-preact'
import { oneDriveAuthApi } from '@/api/oneDriveAuth'
import { OneDriveIcon } from '@/components/common/icons/OneDriveIcon'

export interface OneDriveFile {
    fileId: string
    fileName: string
    mimeType: string
    /**
     * Undefined when the size could not be established — the picker can hand back an item with no
     * drive id, which leaves no Graph lookup to make. Callers must show nothing rather than 0.
     */
    sizeBytes?: number
}

/** Narrows a Graph/picker size field, which may be absent or non-numeric, to a usable count. */
function readSizeBytes(raw: unknown): number | undefined {
    if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return undefined
    return raw
}

export interface OneDriveAuthState {
    isConnected: boolean
    providerEmail: string
    isLoading: boolean
    isConnecting: boolean
    connect: () => Promise<void>
    disconnect: () => Promise<void>
}

interface OneDrivePickerProps {
    auth: OneDriveAuthState
    onFilePicked: (file: OneDriveFile) => void
    onError?: (error: string | undefined) => void
}

const ONEDRIVE_PICKER_BASE_URL = 'https://onedrive.live.com/picker'

function buildPickerOptions(channelId: string) {
    return {
        sdk: '8.0',
        entry: { oneDrive: { files: {} } },
        selection: { mode: 'single' },
        typesAndSources: { mode: 'files' },
        messaging: {
            origin: window.location.origin,
            channelId,
        },
    }
}

function openPickerPopup(options: object, accessToken: string): Window | null {
    const width = 1080
    const height = 680
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const pickerWindow = window.open(
        '',
        'onedrive-picker',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
    )
    if (!pickerWindow) return null

    const queryString = new URLSearchParams({
        filePicker: JSON.stringify(options),
    })
    const url = `${ONEDRIVE_PICKER_BASE_URL}?${queryString}`

    const form = pickerWindow.document.createElement('form')
    form.setAttribute('action', url)
    form.setAttribute('method', 'POST')

    const tokenInput = pickerWindow.document.createElement('input')
    tokenInput.setAttribute('type', 'hidden')
    tokenInput.setAttribute('name', 'access_token')
    tokenInput.setAttribute('value', accessToken)
    form.appendChild(tokenInput)

    pickerWindow.document.body.appendChild(form)
    form.submit()

    return pickerWindow
}

/**
 * `fallback` is the picker's own payload for the same item. Graph is asked because it is
 * authoritative, not because the picker told us nothing — so when the call fails there is no reason
 * to discard what we already had and show the user a raw item id (ORB-721).
 */
async function fetchGraphFileMetadata(
    driveId: string, itemId: string, accessToken: string,
    fallback: { name?: string; size?: number; mimeType?: string }
): Promise<OneDriveFile> {
    const fromPicker = (): OneDriveFile => {
        const fileName = fallback.name || itemId
        return {
            fileId: itemId,
            fileName,
            mimeType: fallback.mimeType || mimeTypeFromExtension(fileName),
            sizeBytes: readSizeBytes(fallback.size),
        }
    }

    // OData's parameter is $select, not select. It has been spelled without the $ since this was
    // written, so Graph ignored the projection and returned the whole item — which is the only reason
    // reading a field outside it ever worked. These three are exactly what is consumed below (ORB-721).
    const graphUrl = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}?$select=name,file,size`
    try {
        const res = await fetch(graphUrl, {
            headers: { Authorization: `Bearer ${accessToken}` },
        })
        // An error status still carries a JSON body, so without this the fields below silently read
        // undefined off it and the file lands named "" rather than falling back.
        if (!res.ok) return fromPicker()

        const meta = await res.json()
        const fileName = meta.name || fallback.name || itemId
        return {
            fileId: itemId,
            fileName,
            mimeType: meta.file?.mimeType || fallback.mimeType || mimeTypeFromExtension(fileName),
            sizeBytes: readSizeBytes(meta.size) ?? readSizeBytes(fallback.size),
        }
    } catch {
        return fromPicker()
    }
}

interface PickerCallbacks {
    onFilePicked: (file: OneDriveFile) => void
    setPickedFile: (file: OneDriveFile) => void
    setIsPicking: (v: boolean) => void
}

function handleAuthenticate(port: MessagePort, messageId: string) {
    oneDriveAuthApi.getPickerToken().then(tokenResp => {
        const token = tokenResp.data?.accessToken
        port.postMessage({
            type: 'result',
            id: messageId,
            data: token
                ? { result: 'token', token }
                : { result: 'error', error: { code: 'tokenError', message: 'No token' } },
        })
    })
}

function handlePick(
    command: { items?: Array<{ id: string; name?: string; size?: number; file?: { mimeType?: string }; parentReference?: { driveId?: string } }> },
    port: MessagePort,
    messageId: string,
    accessToken: string,
    pickerWindow: Window,
    callbacks: PickerCallbacks,
) {
    const items = command.items
    if (items && items.length > 0) {
        const item = items[0]
        const driveId = item.parentReference?.driveId

        if (driveId) {
            fetchGraphFileMetadata(driveId, item.id, accessToken, {
                name: item.name, size: item.size, mimeType: item.file?.mimeType,
            }).then(file => {
                callbacks.setPickedFile(file)
                callbacks.onFilePicked(file)
            })
        } else {
            // No drive id means no Graph call is possible, so the picker's own item payload is all
            // there is. It usually carries a size; when it does not, the size stays unknown.
            const file: OneDriveFile = {
                fileId: item.id,
                fileName: item.name || item.id,
                mimeType: item.file?.mimeType || 'application/octet-stream',
                sizeBytes: readSizeBytes(item.size),
            }
            callbacks.setPickedFile(file)
            callbacks.onFilePicked(file)
        }
    }
    port.postMessage({ type: 'result', id: messageId, data: { result: 'success' } })
    pickerWindow.close()
    callbacks.setIsPicking(false)
    port.close()
}

function createPortMessageHandler(
    getPort: () => MessagePort | null,
    accessToken: string,
    pickerWindow: Window,
    callbacks: PickerCallbacks,
) {
    return (event: MessageEvent) => {
        const message = event.data
        if (message.type === 'notification') return

        if (message.type === 'command') {
            const port = getPort()!
            port.postMessage({ type: 'acknowledge', id: message.id })

            if (message.data.command === 'authenticate') {
                handleAuthenticate(port, message.id)
            } else if (message.data.command === 'pick') {
                handlePick(message.data, port, message.id, accessToken, pickerWindow, callbacks)
            } else if (message.data.command === 'close') {
                pickerWindow.close()
                callbacks.setIsPicking(false)
                port.close()
            }
        }
    }
}

function pollPickerWindow(
    pickerWindow: Window,
    onClosed: () => void,
) {
    const pollTimer = setInterval(() => {
        if (pickerWindow.closed) {
            clearInterval(pollTimer)
            onClosed()
        }
    }, 500)
}

export function OneDrivePicker({ auth, onFilePicked, onError }: OneDrivePickerProps) {
    const { isConnected, providerEmail, isLoading, isConnecting, connect, disconnect } = auth
    const [isPicking, setIsPicking] = useState(false)
    const [pickedFile, setPickedFile] = useState<OneDriveFile | null>(null)

    const openPicker = useCallback(async () => {
        setIsPicking(true)
        try {
            const tokenResponse = await oneDriveAuthApi.getPickerToken()
            if (!tokenResponse.data) {
                onError?.('Failed to get OneDrive access. Please reconnect your account.')
                await disconnect()
                setIsPicking(false)
                return
            }
            const { accessToken } = tokenResponse.data

            const channelId = crypto.randomUUID()
            const options = buildPickerOptions(channelId)
            const pickerWindow = openPickerPopup(options, accessToken)

            if (!pickerWindow) {
                onError?.('Popup was blocked. Please allow popups and try again.')
                setIsPicking(false)
                return
            }

            let port: MessagePort | null = null
            const callbacks: PickerCallbacks = { onFilePicked, setPickedFile, setIsPicking }

            const onPortMessage = createPortMessageHandler(
                () => port, accessToken, pickerWindow, callbacks,
            )

            const handleWindowMessage = (event: MessageEvent) => {
                if (event.source !== pickerWindow) return
                const message = event.data
                if (message.type === 'initialize' && message.channelId === channelId) {
                    port = event.ports[0]
                    port.addEventListener('message', onPortMessage)
                    port.start()
                    port.postMessage({ type: 'activate' })
                    window.removeEventListener('message', handleWindowMessage)
                }
            }

            window.addEventListener('message', handleWindowMessage)

            pollPickerWindow(pickerWindow, () => {
                window.removeEventListener('message', handleWindowMessage)
                port?.close()
                setIsPicking(false)
            })

        } catch (error) {
            console.error('Failed to open OneDrive Picker:', error)
            onError?.('Failed to open OneDrive file picker. Please try again.')
            setIsPicking(false)
        }
    }, [onFilePicked, onError, disconnect])

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking OneDrive connection...
            </div>
        )
    }

    if (!isConnected) {
        return (
            <div className="flex justify-center py-6">
                <button
                    type="button"
                    onClick={() => { onError?.(undefined); connect() }}
                    disabled={isConnecting}
                    className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition-colors disabled:opacity-50"
                >
                    {isConnecting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Connecting...
                        </>
                    ) : (
                        <>
                            <OneDriveIcon className="w-5 h-5" />
                            Sign in with OneDrive
                        </>
                    )}
                </button>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            {/* Connected status */}
            <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">
                    Connected as <span className="font-medium text-slate-700 dark:text-slate-200">{providerEmail}</span>
                </span>
                <button
                    type="button"
                    onClick={disconnect}
                    className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                    <LogOut className="w-3 h-3" />
                    Disconnect
                </button>
            </div>

            {/* Pick file button or picked file display */}
            {pickedFile ? (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20">
                    <OneDriveIcon className="w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                            {pickedFile.fileName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {pickedFile.mimeType}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setPickedFile(null)
                            openPicker()
                        }}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex-shrink-0"
                    >
                        Change
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={openPicker}
                    disabled={isPicking}
                    className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 bg-slate-50 dark:bg-slate-700/30 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-50"
                >
                    {isPicking ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Opening OneDrive...
                        </>
                    ) : (
                        <>
                            <OneDriveIcon className="w-5 h-5" />
                            Pick from OneDrive
                        </>
                    )}
                </button>
            )}
        </div>
    )
}

const EXTENSION_MIME_MAP: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    zip: 'application/zip',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    xml: 'application/xml',
    html: 'text/html',
}

function mimeTypeFromExtension(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || ''
    return EXTENSION_MIME_MAP[ext] || 'application/octet-stream'
}
